import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { F } from 'ts-toolbelt';

import { invariant } from '@e-socna/utils';
import { getDocumentName } from '@e-socna/data-layer';
import type { Id, PennylaneAggregationLevelEnum } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { MissionAccountingPage_PennylaneAccounting_QueryDocument } from '../../../pages/MissionAccounting/hooks/useMissionPennylaneAccountingQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { FolderBusinessEntityStateFormValues } from '../../FolderBusinessEntityState/types/values.type';
import type { UseUpdateFolderBusinessEntityStateSubmit } from '../../FolderBusinessEntityState/types/use-submit.type';

import { useUpdateMissionAccountingInfoStateMutation } from './useUpdateMissionAccountingInfoStateMutation.hook';
import type { MissionAccountingStateForm_UpdateAccountingInfoState_Result } from './useUpdateMissionAccountingInfoStateMutation.hook';

export const useUpdateMissionAccountingInfoStateSubmitFactory = ({
  missionId,
  onSuccess: handleSuccessFactoryLevel,
}: Readonly<{
  missionId: Id;
  onSuccess?: (params: Readonly<{ actionId: Id; newStateId: Id }>) => void;
}>): UseUpdateFolderBusinessEntityStateSubmit =>
  function useUpdateMissionAccountingInfoStateSubmit({
    memos,
    onSuccess: handleSuccessSubmitLevel,
  }: F.Parameters<UseUpdateFolderBusinessEntityStateSubmit>[0]) {
    const [formError, setFormError] = useState<readonly string[] | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { mutate: updateMissionAccountingInfoStateMutation } =
      useUpdateMissionAccountingInfoStateMutation();

    const handleSubmit = useCallback(
      (
        values: Readonly<FolderBusinessEntityStateFormValues>,
        {
          setSubmitting,
        }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
      ) => {
        setFormError(null);

        const onSuccess = (
          result: Readonly<MissionAccountingStateForm_UpdateAccountingInfoState_Result>,
        ) => {
          enqueueSnackbar(
            'Le statut de la comptabilité a bien été mis à jour.',
          );

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MissionPage_OneMission_QueryDocument),
              {
                id: missionId,
              },
            ],
          });

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(
                MissionAccountingPage_PennylaneAccounting_QueryDocument,
              ),
              {
                id: missionId,
              },
            ],
          });

          invariant(
            !!result.updateAccountingInfoState.accountingInfo,
            'Accounting info not found',
          );

          handleSuccessFactoryLevel?.({
            actionId: values.actionId,
            newStateId: result.updateAccountingInfoState.accountingInfo.stateId,
          });

          handleSuccessSubmitLevel?.();

          setSubmitting(false);
        };

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));

          setSubmitting(false);
        };

        const { aggregationLevel, ...restValues } = values as Readonly<
          FolderBusinessEntityStateFormValues & {
            aggregationLevel?: PennylaneAggregationLevelEnum;
          }
        >;

        updateMissionAccountingInfoStateMutation(
          {
            missionId,
            ...restValues,
            aggregationLevel,
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onSuccess,
            onError,
          },
        );
      },
      [
        enqueueSnackbar,
        handleSuccessSubmitLevel,
        memos,
        queryClient,
        updateMissionAccountingInfoStateMutation,
      ],
    );

    return {
      formError,
      handleSubmit,
    };
  };
